import GameManager from '../../cvp-webgames-core/GameManager';
import BaseScenarioPart from '../../cvp-webgames-core/Scenario/BaseScenarioPart';
import { DaarcBaseScenario } from '../DaarcBaseScenario';
import { constants } from '../../misc/constants';
import { DebriefIntro, DebriefVortex } from '../../components/ui/DebriefUI';
import { AssessAirwayActionData, AssessAirwayActions } from '../../components/ui/AssessAirwayUI';
import { VortexUICurrentAttempt } from '../../components/ui/hud/VortexUI';
import BaseTestScenario from '../base-test-scenario/BaseTestScenario';
import { HudActions, HudNPCType } from '../../components/ui/hud/HUDUI';

export default class EmanuelMcKenzie extends BaseTestScenario {

	public isSgaSize: boolean;
	public isSgaString: any;
	public isHumidityString: string;


	public onBVMOptimizationChooseAnotherTechniqueVideoChapter(): string {
		return 'DAARC - Scenario8 - 45 - BVM - Squeezing Itself - 8';
    }

	public onETTOptimizationChooseAnotherTechniqueVideoChapter(): string {
		return 'DAARC - Scenario8 - 45 - ETT - No PreOx - 13';
    }

	public onSGAOptimizationChooseAnotherTechniqueVideoChapter(): string {
		return 'DAARC - Scenario10 - 45 - SGA - No PreOx - 16';
    }

	public get maxScore(): number {
		return 53600;
	}

	public get predeterminedPoints(): number {
		var assessPatientRemoveDenturesPoints = 500;
		var setupFiberOpticsPoints = 1000;
		var prePoints = 3900;
		var awardedPoints = 32700;
		var removedPoints = 1400;

		return assessPatientRemoveDenturesPoints + awardedPoints +
			setupFiberOpticsPoints + prePoints + removedPoints;
	}

	public get totalNumberOfQuestions(): number {
		return 5;
	}

	public get currentLevel(): number {
		return 8;
	}

	public getDefaultApneaTime(): number {
		return 210
	}

	public getDefaultSa02(): number {
		return 85
	}

	constructor(gameManager: GameManager, scenarioName: string) {
		super(gameManager, scenarioName);

		this.isEmanuelMcKenzie = true;

		this._setupIntro();
		this._setupGeneralNeutralState();
		this._setupProcedures();
	}

	public getDebriefIntro(): DebriefIntro {
		return {
			items: [
				"The patient’s past medical history indicates possible tumor recurrence and upper airway obstruction to both ventilation and intubation.",
                "This patient is coding, likely due to respiratory arrest. The need to move quickly through the Vortex pathway is paramount.",
                "Did you factor in the clinical condition of the patient in your utilization of the Vortex approach?"
			],
			link: 'https://google.com'
		};
	}

	public getDebriefVortex(): DebriefVortex {
		return {
			successItems: [
				'BVM: ' + this.bvmAttemptCount + ' attempt(s)',
				'ETT: ' + this.ettAttemptCount + ' attempt(s)',
				'SGA: ' + this.sgaAttemptCount + ' attempt(s)',
				'Cricothyrotomy: ' + this.cricAttemptCount + ' attempt(s)'
			],
			askYourselfItems: [
				'Did you exceed the maximum number of optimizations (3) for a given attempt?',
				'Did you optimize between each attempt at the bag valve mask, intubation and supraglottic airway?',
				'Did you follow the Vortex approach by not returning to a technique that you had already attempted with appropriate optimizations and failed?',
				'How can you more efficiently manage the airway? Remember that the quicker you establish a stable, patent airway, the better the patient outcome.'
			]
		};
	}

	public onBVMFinished(): void {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.BVM, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onETTFinished(): void {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.ETT, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onSGAFinished(): void {
		if(this.isSgaSize) {
			this.addScore(this.predeterminedPoints);
			//this._CheckOxy();
			this.showSuccessPopUpAfterAuscultate(VortexUICurrentAttempt.SGA, 'You successfully established a stable, patent airway for Emanuel McKenzie ' + this.apneaTimeElapsed + ' after he became apneic. A team arrives shortly thereafter to establish a definitive airway for Mr. McKenzie. You earned ' + this.totalScore + ' out of a possible ' + this.maxScore + ' points. Review the debrief for a summary of key learning points.');
		} else this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.SGA, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onCricFinished(): void {
		this.cricAttemptCount = 1;
		//this._CheckOxy();
		this.showFailedScenarioPopUpAfterAuscultate(VortexUICurrentAttempt.Cric, 'You should not have attempted a cricothyrotomy. A stable, patent airway can be established through non-surgical attempts. Review the debrief and then try again.');
	} //cric failed text is being used as a general placeholder. OG game had cric as success so SME's still need to provide the new fail text

	public _CheckOxy() {
		if(!this.preOxDone) {
			this.debriefLogFailedItem("Pre-Oxygenation: You did not pre-oxygenate the patient.")
		};
		if(!this.callFlexBr) {
			this.debriefLogFailedItem("Call for flexible bronchoscope: Recognizing the need for a flexible bronchoscope early is critical, especially when the patient’s condition indicates intubation and use of a supraglottic airway will be anatomically impossible.")
		}	
	}

	public getAssessAirwayData(): AssessAirwayActionData[] {
		return [
			{
				relatedAction: AssessAirwayActions.ExamineNeck,
				title: 'Examine Neck',
				text: 'The trachea is immobile and fixed at the midline; scar tissue is visible over the skin.'
			},
			{
				relatedAction: AssessAirwayActions.LiftChinToCeiling,
				title: 'Lift chin to ceiling',
				text: 'The patient can\'t respond. You must manually extend the patient\'s neck.'
			},
			{
				relatedAction: AssessAirwayActions.PutChinToChest,
				title: 'Put chin to chest',
				text: 'The patient can\'t respond'
			},
			{
				relatedAction: AssessAirwayActions.RemoveDentures,
				title: 'Remove Dentures',
				text: 'The patient can\'t respond',
				noPoints: true
			},
			{
				relatedAction: AssessAirwayActions.ShowTeeth,
				title: 'Smile for me',
				text: 'The patient can\'t respond'
			},
			{
				relatedAction: AssessAirwayActions.StickOutTongue,
				title: 'Open mouth',
				text: 'The patient can\'t respond'
			}
		]
	}

	private _setupIntro() {
		// this.intro.config.part1Config = {
		// 	videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
		// 	dialogs: [
		// 		{
		// 			speakerName: constants.speakers.RnMiranda,
		// 			text: `
		// 			This is a 65-year-old male who is coding. He was admitted for 
		// 			failure to thrive and I found him unresponsive and called the code. 
		// 			The respiratory therapist is making an attempt to ventilate the patient.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario8 - intro - 53'
		// 		},
		// 		{
		// 			speakerName: constants.speakers.Yamal,
		// 			text: `
		// 			I have an OPA in place and I still can’t move anything!
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario8 - intro - 53'
		// 		}
		// 	],
		// 	sbarConfig: {
		// 		s: `This patient is coding. The respiratory therapist is unable to ventilate the patient using a bag valve mask with an OPA.`,
		// 		b: `
		// 			Age: 65<br />
		// 			Height (in): 65 in<br />
		// 			Weight (kg): 50 kg<br />
		// 			Potassium (K): 2.8<br />
		// 			NPO Status: NPO<br />
		// 			<br />
		// 			PMH: Pharyngeal tumor; remote; status post-surgical resection & radiation\nPatient came in for failure to thrive, 
		// 			can’t eat, and is lethargic. Patient admitted for dehydration. Patient has been receiving IV fluids. 
		// 			The nurse found him unresponsive, called the code, and ACLS has been initiated.
		// 		`,
		// 		a: `Emanuel McKenzie needs a patent airway.`,
		// 		r: `Manage the patient's airway during cardiac arrest.`
		// 	},
		// 	onSbarClosed: () => {
		// 		const context = this;
		// 		setTimeout(() => {
		// 			context.sideMenuId = context.uiWrapper.displaySideMenu({
		// 				updatedStuff: {
		// 					inductionTimeInSeconds: 0,
		// 					apneaTimeInSeconds: 360,
		// 					isApneaTimerIdle: false
		// 				}
		// 			}, context.sideMenuId);
		// 		}, 100);
		// 	}
		// }

		this._id_ScenarioSet(8); // <<<<<< CRITICAL: must be set in every scenario. This number does not always match the "level number" variable set in the declarations above, use the documented level numbers and not how they are listed in game <<<<<<<<

		this.intro.config.part1Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			chapters: [
				{
					chapterName: () => 'DAARC - Scenario8 - intro - 53'
				}
			],
			sbarConfig: {
				s: `This patient is coding. The respiratory therapist is unable to ventilate the patient using a bag valve mask with an OPA.`,
				b: `
					Age: 65<br />
					Height (in): 65 in<br />
					Weight (kg): 50 kg<br />
					Potassium (K): 2.8<br />
					NPO Status: NPO<br />
					<br />
					PMH: Pharyngeal tumor; remote; status post-surgical resection & radiation\nPatient came in for failure to thrive, 
					can't eat, and is lethargic. Patient admitted for dehydration. Patient has been receiving IV fluids. 
					The nurse found him unresponsive, called the code, and ACLS has been initiated.
				`,
				b508: `
					Age. 65.
					Height. 65 inches.
					Weight. 50 kilograms.
					Potassium. 2.8.
					NPO Status. NPO.
					Past Medical History. Pharyngeal tumor  remote  status post-surgical resection and radiation Patient came in for failure to thrive, 
					cant eat, and is lethargic. Patient admitted for dehydration. Patient has been receiving IV fluids. 
					The nurse found him unresponsive, called the code, and ACLS has been initiated.
				`,
				a: `Emanuel McKenzie needs a patent airway.`,
				r: `Manage the patient's airway during cardiac arrest.`
			},
			onSbarClosed: () => {
				const context = this;
				setTimeout(() => {
					context.sideMenuId = context.uiWrapper.displaySideMenu({
						updatedStuff: {
							inductionTimeInSeconds: 0,
							apneaTimeInSeconds: 210,
							isApneaTimerIdle: false
						}
					}, context.sideMenuId);
				}, 100);
			}
		}
	}

	private _setupGeneralNeutralState() {
		const context = this;
		this.neutralState.config.isEmanuelMcKenzie = true;
		this.neutralState.config.disabledNPCs = [
			HudNPCType.respiratoryTherapist,
			HudNPCType.physician
		];
		this.neutralState.config.disabledActions = [
			HudActions.AdjustBedHeight,
			HudActions.PreOxygenatePatient
		];
		this.neutralState.config.part1Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			hudIdleVideoChapter: () =>'DAARC - Scenario8 - 45 - BVM - Squeezing Itself - 8',//yes it is unique and special..... BETACHECK
			onCalledForVortex: () => {
				this.debriefLog('Called for a Vortex reader')
			}
		};

		var toolsArr = null
		if(this.isLite) {
			toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,	
				//constants.toolbagTools.cric //comment this out on cric not required scenarios		
			]
		} else {			
				toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,
				constants.toolbagTools.cric
			]
		}

		this.neutralState.config.part2Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			toolsToChooseFrom: toolsArr,
			onTeamNotDebriefed: () => {
				this.debriefLogFailedItem("Brief other staff in the room about attempt order: Briefing your staff on the attempt order will help them anticipate providing the right equipment. Briefings empower your staff to notify you if you fall into fixation error.")
            },
            onTeamDebriefed: () => {
                this.debriefLog('Briefed other staff in the room about attempt order');
            },
            onToolsOrganized: () => {
                this.debriefLog('Organized equipment');
            }
		};

		this.neutralState.config.part3Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			waitForSuctionVideoChapter: () =>'DAARC - Scenario8 - 45 - BVM - Squeezing Itself - 8', //BETACHECK
			onSuctionSet: () => {
				this.debriefLog("Suction Set") 
			}
		};

		this.neutralState.config.part4Config = { //ACTION WONT PERFORM DURING THIS SCENARIO
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			waitForAdjustBedHeightVideoChapter: () =>'DAARC - Scenario8 - 45 - BVM - Squeezing Itself - 8', 
			onAdjustedBedHeight: () => {
				this._id_bedPos = this._id_bedAdj; // <<<<<<<
				this.debriefLog("Bed Adjusted");
			}
		};

		this.neutralState.config.part5Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			liftChinVideoChapter: () =>'DAARC - Scenario8 - 45 - patient-assessment-up - 36',//this patient is always flat, it is only a title
			chinToChestVideoChapter: () =>'DAARC - Scenario8 - 45 - patient-assessment-down - 37',//this patient is always flat, it is only a title
			examineNeckVideoChapter: () =>'DAARC - Scenario8 - 45 - patient-assessment-straight - 35',//this patient is always flat, it is only a title
			realTeethVideoChapter: () =>'DAARC - Scenario8 - 45 - patient-assessment-straight - 35',//this patient is always flat, it is only a title
			stickOutTongueVideoChapter: () =>'DAARC - Scenario8 - 45 - patient-assessment-straight - 35',//this patient is always flat, it is only a title
			onAssessmentFinied: () => {
				this.debriefLog("Performed a complete airway assessment")
			}
		};

		this.neutralState.config.part6Config = { //ACTION WONT PERFORM DURING THIS SCENARIO
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			waitForFiberOpticVideoChapter: () =>'DAARC - Scenario8 - 45 - BVM - Squeezing Itself - 8', //BETACHECK
			onSetupFiberOptic: () => {
				this.callFlexBr = true;
				this.debriefLog("Called for advanced airway cart")
			}
		};

		this.neutralState.config.part7Config = { // ACTION WON'T PERFORM DURING THIS SCENARIO
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			waitForPreOxygenateVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText),//'DAARC - Scenario8 - intro - 53',
			onPreOxygenated: (selection: number) => {
				this.preOxDone = true;
				this._id_UpdatePreOxText(selection);
				switch(selection) {
					case 0:
						this.debriefLog("Pre-Oxygenated with High Flow Nasal Cannula");
						break;
					case 1:
						this.debriefLog("Pre-Oxygenated with Nasal Noninvasive Positive Pressure");
						break;
					case 2:
						this.debriefLog("Pre-Oxygenated with 100% Non-Rebreather Mask");
						break;
					case 3:
						this.debriefLogFailedItem("Pre-Oxygenated with BVM. This may not be the best solution");
						break;
				}
			}
		};

		this.neutralState.config.part8Config = { // PATIENT DIES IMMEDIATELY IF DONE DURING THIS SCENARIO
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			onApneaStartedVideoChapter: () =>'DAARC - Scenario8 - 45 - patient-assessment-straight - 35',//this patient is always flat, it is only a title
			onSingleMedAdministered: () => {
				this._CheckOxy();
				context.showFailedScenarioPopUp('You administered an induction agent to a patient in cardiac arrest. The patient will never recover. Review the debrief and then try again.')
			},
			meds: [
				{
					title: 'Induction Agent',
					audioPrefix: 'InductionAgent_',

				},
				{
					title: 'Administer Neuromuscular Junction Blocker',
					audioPrefix: 'NMJB_'
				}
			]
		};
	}

	private _setupProcedures() {
		this._setupBvm();
		this._setupEtt();
		this._setupSga();
		this._setupCric();
		this._setupAuscultate();
	}

	private _setupBvm() {
		this.procedures.bvm.config.part1Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			chooseHandVideoChapter: () => this._id_bvmHandAttempt,
			onChooseHandNextClicked: (isOneHanded: boolean) => {
				if(isOneHanded) {
					this._id_bvmHandAttempt = 'DAARC - Scenario8 - 45 - bvm-attempt-onehanded-no-pre-oxy - 39'
				} else {
					this._id_bvmHandAttempt = 'DAARC - Scenario8 - 45 - bvm-attempt-twohanded-no-pre-oxy - 38'
				}
			}
		};
		this.procedures.bvm.config.part2Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			onProcedureAttempted: (attemptCount: number) => {
				this.bvmAttemptCount = attemptCount;
			}
		}
	}

	private _setupEtt() {
		this.procedures.ett.config.part1Config = {
			hideVlButton: () => true,
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			squeezeAmbuBagVideoChapter: () => 'DAARC - Scenario8 - 45 - ett-nothing - 47',	//'DAARC - Scenario8 - intro - 53',
			insertingETTVideoChapter: () => 'DAARC - Scenario8 - 45 - ett-nothing - 47',	//'DAARC - Scenario8 - intro - 53',
			ettSpecialUIConfig: {
				airwayImage: () => {
					return './assets/images/ett/Grade4Tumor.png';
				},
				magillForceps: {
					text: () => 'There is no foreign body in the patient’s airway.'
				},
				suction: {
					text: () => ''
				},
				manipulateLarynx: {
					text: () => 'The patient’s trachea cannot be moved.'
				}
			},
			onProcedureAttempted: (attemptCount: number) => {
				this.ettAttemptCount = attemptCount;
			}
		};
	}

	private _setupSga() {
		this.procedures.sga.config.isAttemptSuccessful = () => {
			return this.isSgaSize;
		}

		this.procedures.sga.config.part1Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			squeezeAmbuBagVideoChapter: () => 'DAARC - Scenario8 - 45 - sga-nothing - 50',
			insertingSgaVideoChapter: () => 'DAARC - Scenario8 - 45 - sga-nothing - 50',
			//'DAARC - Scenario8 - intro - 53',
			onMakeAttemptClicked: (selectedSGA: string) => {
				if(selectedSGA === '4') {
					this.isSgaSize = true;
				} else this.isSgaSize = false;
			},
			onProcedureAttempted: (attemptCount: number) => {
				this.sgaAttemptCount = attemptCount;
			}
		};
	}

	private _setupCric() {
		const context = this;

		this.procedures.cric.config.part1Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			correctToolOrder: [
				'Scalpel',
				'Bougie',
				'ETT',
				'Spreader',
				'Syringe',
			]
		};
		this.procedures.cric.config.part2Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			identifyLandmarksVideoChapter: () =>this._id_SegmentAssembly(this._id_CricPart(1), 'flat'),//'DAARC - Scenario8 - intro - 53',
			areLandmarksObstructed: true,
			landmarksObstructedMessageText: 'Unable to identify landmarks due to the large mass.',
			landmarksObstructedMessageTitle: 'Landmark Identification Obstructed'			
		};
		this.procedures.cric.config.part3Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4'
		};
		this.procedures.cric.config.part4Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			scalpelVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(2), 'flat'),//'DAARC - Scenario8 - intro - 53',
			onMakingIncition: () => {
				this.cricAttemptCount++;
				//this._CheckOxy();
				context.showFailedScenarioPopUp('You could only feel the large mass and the surgical landmarks were not visible or palpable. In these cases, it’s unwise to attempt a cricothyrotomy. As a result, Emanuel McKenzie expired. Review the debrief and then try again.') 
			}	
		};
		this.procedures.cric.config.part5Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			spreaderVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(3), 'flat'),//'DAARC - Scenario8 - intro - 53'
		};
		this.procedures.cric.config.part6Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			bougieVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(4), 'flat'),//'DAARC - Scenario8 - intro - 53'
		};
		this.procedures.cric.config.part7Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			etTubeVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(5), 'flat')//'DAARC - Scenario8 - intro - 53'
		};
		this.procedures.cric.config.part8Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(6), 'Flat')//'DAARC - Scenario8 - intro - 53'
		};
	}

	private _setupAuscultate() {		
		this.procedures.auscultate.config.part1Config = {
			videoUrl: './assets/videos/scenario8/daarc-scenario8-emanuelmckenzie-v1.mp4',
			bvmConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_bvmAusc,'45'),//'DAARC - Scenario8 - intro - 53',
				checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathNo
				}
			},
			ettConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_ettAusc,'45'),//'DAARC - Scenario8 - intro - 53',
				checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathNo
				}
			},
			sgaConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_sgaAusc,'45'),//'DAARC - Scenario8 - intro - 53',
				checkRiseAndTubeHumidificationText: () => {
					if(this.isSgaSize){
						return this.auscSuccess
					} else return this.auscFailed
				},
				audioPath: () => {
					if(this.isSgaSize) {
						return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
					} else return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					if(this.isSgaSize) {
						return isYesClicked;
					} else return !isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					if(this.isSgaSize){
						return this.hoverBreathYes
					} else return this.hoverBreathNo
				}
			},
			cricConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_cricAusc, 'flat'),//'DAARC - Scenario8 - intro - 53',
				checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathNo
				}
			}
		}
	}


	public getParts(): BaseScenarioPart<DaarcBaseScenario>[] {
		return this.parts;
	}

	public getSoundPath(fileName: string): string {
		return './assets/audio/scenario1/' + fileName + '.ogg';
	}

	public showDefaultSideMenu() {
		const context = this;
		context.sideMenuId = context.uiWrapper.displaySideMenu({
			gameManager: this.gameManager,
			inductionTimeInSeconds: 0,
			apneaTimeInSeconds: 210,
			bloodPressureNumerator: 0,
			bloodPressureDenominator: 0,
			bloodPressureMinNumerator: 50,
			bloodPressureMinDenominator: 33,
			bloodPressureMaxNumerator: 220,
			bloodPressureMaxDenominator: 110,
			sa02: 0,
			heartRate: 0,
			heartRateMax: 200,
			respirationRate: 0,
			resetEtc02: false,
			etc02: 0,
			showETC02: false,
			isApneaTimerIdle: true,
			onInductionTimerRanOut: () => {
				context.hasInductionTimerRanOut = true;
			},
			onApneaTimerRanOut: () => {
                context.failedScenario = true;
				//this._CheckOxy();
				context.onShowSuccessFailedAttempt(constants.attemptText.failedTitle, 'You did not establish a stable, patent airway in time for Emanuel McKenzie. He has expired. Review the debrief and then try again.', true);
			}
		}, context.sideMenuId);
	}
}